import type { LinearRGB } from '../../../core/color';
import type { Landmark } from '../../../data/types';
import type { MeshBuilder } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import type { ChurchParams } from './churchParams';
import { dominantAxis, meshFrameOf, planFrameOf, type PlanFrame } from './planFrame';
import { planChurch, type ChurchPlan } from './churchPlan';
import { buildVessel, type VesselOptions } from './churchVessel';
import {
  buildChapel,
  buildChapelPinnacles,
  buildHallAisle,
  buildLeanTo,
  buildTransept,
  SIDE_PARTS,
  type BodyReach,
} from './churchSideParts';
import { buildFleche, buildWestTower } from './churchTower';
import type { MasonryStyles } from './masonry';

export interface ChurchColors {
  readonly wall: LinearRGB;
  readonly roof: LinearRGB;
  readonly opening: LinearRGB;
}

const CLERESTORY = {
  sillAboveAisle: 0.8,
  topDrop: 0.8,
  plainSill: 2.5,
  plainTopDrop: 1,
} as const;

function churchStyles(
  colors: ChurchColors,
  plan: ChurchPlan,
  edgeId: number,
  seed: number
): MasonryStyles {
  const length = plan.vessel.chordU - plan.vessel.u0;
  const slope = Math.hypot(plan.vessel.half, plan.vessel.ridgeY - plan.vessel.eaveY);
  const base = { edgeId, seed, strokeAngle: 0 };
  return {
    wall: {
      ...base,
      color: colors.wall,
      surface: SURFACE.stone,
      extent: [length, plan.vessel.eaveY],
    },
    roof: { ...base, color: colors.roof, surface: SURFACE.roofSlate, extent: [length, slope] },
    opening: { ...base, color: colors.opening, surface: SURFACE.linework, extent: [1, 1] },
  };
}

function vesselOptions(plan: ChurchPlan): VesselOptions {
  const { vessel, sides } = plan;
  const hall = sides.some((s) => s.roof === 'gable');
  const aisleTop = Math.max(0, ...sides.map((s) => s.topY));
  const longWindows = hall
    ? null
    : sides.length > 0
      ? { sill: aisleTop + CLERESTORY.sillAboveAisle, top: vessel.eaveY - CLERESTORY.topDrop }
      : { sill: CLERESTORY.plainSill, top: vessel.eaveY - CLERESTORY.plainTopDrop };
  return {
    longWindows,
    westWindow: plan.tower === null && plan.westBay === null,
    bay: SIDE_PARTS.bay,
  };
}

function bodyReach(plan: ChurchPlan): BodyReach {
  const { vessel } = plan;
  const vs = [
    vessel.vc - vessel.half,
    vessel.vc + vessel.half,
    ...plan.sides.flatMap((s) => [s.inner, s.outer]),
    ...plan.chapels.flatMap((c) => [c.inner, c.outer]),
  ];
  return { low: Math.min(...vs), high: Math.max(...vs) };
}

/** The church's plan frame (turned onto its traced walls) and the parts placed in it. */
export function churchPlanOf(
  landmark: Landmark,
  params: ChurchParams
): { plan: ChurchPlan; frame: PlanFrame } {
  const frame = planFrameOf(landmark.footprint, dominantAxis(landmark.footprint, landmark.heading));
  return { plan: planChurch(frame, params), frame };
}

export interface PlannedChurch {
  readonly plan: ChurchPlan;
  readonly frame: PlanFrame;
}

/** Builds the parts of a church already placed in its plan frame. */
export function buildChurchPlan(
  builder: MeshBuilder,
  { plan, frame }: PlannedChurch,
  colors: ChurchColors,
  edgeId: number,
  seed: number
): number {
  const mesh = meshFrameOf(frame);
  const styles = churchStyles(colors, plan, edgeId, seed);
  const before = builder.triangleCount;
  if (plan.tower) buildWestTower(builder, styles, mesh, plan.tower);
  if (plan.westBay)
    buildVessel(builder, styles, mesh, plan.westBay, {
      longWindows: null,
      westWindow: false,
      bay: SIDE_PARTS.bay,
    });
  buildVessel(builder, styles, mesh, plan.vessel, vesselOptions(plan));
  for (const side of plan.sides) {
    if (side.roof === 'leanTo') buildLeanTo(builder, styles, mesh, side);
    else buildHallAisle(builder, styles, mesh, side);
  }
  for (const chapel of plan.chapels) buildChapel(builder, styles, mesh, chapel);
  buildChapelPinnacles(builder, styles, mesh, plan.chapels);
  if (plan.transept) buildTransept(builder, styles, mesh, plan.transept, bodyReach(plan));
  if (plan.fleche) buildFleche(builder, styles, mesh, plan.fleche, plan.vessel.half);
  return builder.triangleCount - before;
}

export function buildChurch(
  builder: MeshBuilder,
  landmark: Landmark,
  params: ChurchParams,
  colors: ChurchColors,
  edgeId: number,
  seed: number
): number {
  return buildChurchPlan(builder, churchPlanOf(landmark, params), colors, edgeId, seed);
}
